import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Query, QueryKey } from "@tanstack/react-query";

import { useAuthStore } from "@/features/auth/store";

import { conversationsApi } from "./api";

const LIST_KEY = ["whatsapp-conversations"] as const;

// When each polled query's data last actually changed. React Query keeps the
// same data reference when a refetch returns identical JSON, so a new
// reference means something new arrived.
const lastChange = new Map<string, { data: unknown; at: number }>();

/**
 * Poll fast while a thread is active and back off as it goes quiet, so a tab
 * left open all day stays well under the API's per-user request limit.
 * Stops on an error (401 after the session expires, 429 when throttled) so a
 * logged-out tab doesn't keep burning the anonymous limit the login page
 * needs; React Query resumes on window focus or reconnect. Hidden tabs are
 * already paused (refetchIntervalInBackground defaults to false).
 */
function adaptiveInterval(fastMs: number, midMs: number, slowMs: number) {
  return <T>(query: Query<T, Error, T, QueryKey>) => {
    if (query.state.status === "error") return false;
    const now = Date.now();
    const prev = lastChange.get(query.queryHash);
    if (!prev || prev.data !== query.state.data) {
      lastChange.set(query.queryHash, { data: query.state.data, at: now });
      return fastMs;
    }
    const idle = now - prev.at;
    if (idle < 2 * 60_000) return fastMs;
    if (idle < 10 * 60_000) return midMs;
    return slowMs;
  };
}

export function useConversations() {
  const isAuthed = !!useAuthStore((s) => s.user);
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: conversationsApi.list,
    enabled: isAuthed,
    // New customer messages arrive via webhook — poll to surface them.
    // Mounted app-wide (alerts + sidebar badge), so it backs off when quiet.
    refetchInterval: adaptiveInterval(10_000, 30_000, 60_000),
    retry: 1,
  });
}

export function useConversationMessages(id: string | null) {
  const isAuthed = !!useAuthStore((s) => s.user);
  return useQuery({
    queryKey: ["whatsapp-messages", id],
    queryFn: () => conversationsApi.messages(id!),
    enabled: isAuthed && !!id,
    refetchInterval: adaptiveInterval(5_000, 15_000, 30_000),
    retry: 1,
  });
}

export function useSendMessage(id: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => conversationsApi.send(id!, body),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["whatsapp-messages", id] });
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

export function useToggleAi(id: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ai_enabled: boolean) =>
      conversationsApi.update(id!, { ai_enabled }),
    onSettled: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conversationsApi.remove(id),
    onSuccess: (_data, id) => {
      // Drop the thread's cached messages too — the list polls every few
      // seconds and would otherwise refill a pane for a thread that's gone.
      qc.removeQueries({ queryKey: ["whatsapp-messages", id] });
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}
